import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Select as AntdSelect,
  Button,
  Form,
  Input,
  Space,
  Typography
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React, { useEffect } from 'react';
import {
  ASSET_CATEGORY,
  FORM_CTA_TYPES,
  GRID_TYPE,
  MAX_LENGTHS,
  MODULE_TYPES,
  PAGE_TYPES,
  PAGE_TYPES_OPTIONS,
  STATIC_PAGES,
  STATUS_TYPES
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import SelectAsset from '../../../../assets/components/SelectAsset';
import { GET_SLUGS } from '../../../../menus/graphql/Queries';
import { Select } from '../../../../videos/components/FormInputs';
import { FORM_TYPES } from '../../../context/EditPageProvider';
import {
  CREATE_PAGE_MODULE,
  UPDATE_PAGE_MODULE
} from '../../../graphql/Mutations';
import { ACTION_TYPES } from './CTAForm';
import { ModuleFields, Permissions, ShowFields } from './FormInputs';

const initialValues = {
  title: '',
  description: '',
  status: STATUS_TYPES.PUBLISHED,
  permissions: [],
  settings: {
    description: true,
    itemImage: true,
    itemTitle: true,
    title: true
  },
  config: {
    items: [],
    type: GRID_TYPE.BUTTON
  }
};
const CTA_TITLE_MAX_LENGTH = 50;

const CONFIG_TITLE = {
  [MODULE_TYPES.CTA_BUTTON_LIST]: 'CTA Button List'
};

const MODULE_KEYS = {
  [MODULE_TYPES.CTA_BUTTON_LIST]: 'ctaButtonListModule'
};

const MODULE_TYPE_KEYS = {
  [MODULE_TYPES.CTA_BUTTON_LIST]: 'CTABlockActionType'
};

const SETTINGS = [
  {
    name: 'bottomSeparator',
    label: 'Bottom Separator',
    allowedTypes: [MODULE_TYPES.CTA_BUTTON_LIST]
  }
];

const ACTION_TYPE_OPTIONS = [
  { label: 'Internal', value: ACTION_TYPES.INTERNAL },
  { label: 'External', value: ACTION_TYPES.EXTERNAL }
];

const ItemInput = ({ name, remove, form, fieldKey, type: moduleType }) => {
  const { internalPageType, type } =
    Form.useWatch(['config', 'items', fieldKey?.toString()], form) ?? {};
  return (
    <>
      <Space className="w-full" direction="vertical">
        <Form.Item
          label="Title"
          name={[name, 'title']}
          rules={[formValidatorRules?.maxLength(CTA_TITLE_MAX_LENGTH)]}
        >
          <Input placeholder="Enter title" />
        </Form.Item>
        {moduleType !== MODULE_TYPES.CTA_BUTTON_LIST ? (
          <Form.Item label="Description" name={[name, 'description']}>
            <TextArea placeholder="Enter description" />
          </Form.Item>
        ) : (
          <Form.Item
            label="Button Type"
            name={[name, 'buttonType']}
            rules={[formValidatorRules?.maxLength(MAX_LENGTHS.CTA)]}
          >
            <AntdSelect options={FORM_CTA_TYPES} placeholder="Select page" />
          </Form.Item>
        )}
        <Form.Item label="Type" name={[name, 'type']}>
          <AntdSelect
            options={ACTION_TYPE_OPTIONS}
            onChange={() => {
              form.resetFields([
                [name, 'internalPageType'],
                [name, 'slug'],
                [name, 'url']
              ]);
            }}
            placeholder="Enter Type"
          />
        </Form.Item>
        {type === ACTION_TYPES.INTERNAL && (
          <>
            <Form.Item label="Page Type" name={[name, 'internalPageType']}>
              <AntdSelect
                options={PAGE_TYPES_OPTIONS}
                placeholder="Select page type"
                onChange={() => {
                  form.resetFields([
                    [name, 'slug'],
                    [name, 'url']
                  ]);
                }}
              />
            </Form.Item>
            {internalPageType && (
              <>
                {internalPageType === PAGE_TYPES.STATIC ? (
                  <Form.Item label="Page" name={[name, 'slug']}>
                    <AntdSelect
                      options={STATIC_PAGES}
                      onChange={(value) => {
                        form.setFieldValue(
                          ['config', 'items', name, 'url'],
                          `${value}`
                        );
                      }}
                      placeholder="Select page"
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Page" name={[name, 'slug']}>
                    <Select
                      placeholder="Select"
                      query={GET_SLUGS}
                      variablesSelector={(filter) => ({
                        filter: {
                          ...filter,
                          pageType: internalPageType
                        }
                      })}
                      dataSelector={(data) => {
                        return (
                          data?.slugs?.slugs?.map(({ slug }) => ({
                            label: `/${slug}`,
                            value: slug
                          })) ?? []
                        );
                      }}
                      keys={{
                        data: 'slugs',
                        records: 'slugs',
                        count: 'count'
                      }}
                      onChange={({ value }) => {
                        form.setFieldValue(
                          ['config', 'items', name, 'url'],
                          `${value}`
                        );
                      }}
                    />
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}

        <Form.Item label="URL" name={[name, 'url']}>
          <Input
            readOnly={type === ACTION_TYPES.INTERNAL}
            disabled={type === ACTION_TYPES.INTERNAL}
            placeholder="Enter url"
          />
        </Form.Item>
        {moduleType === MODULE_TYPES.CTA_BUTTON_LIST ? (
          <Form.Item label="Icon" name={[name, 'asset']}>
            <SelectAsset
              modalTitle="Select Icon"
              categoryKey={ASSET_CATEGORY?.ICON}
              btnText="Icon"
              dataSelector={({ id, url }) => ({
                id,
                url
              })}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Images" name={[name, 'asset']}>
            <SelectAsset
              modalTitle="Select Image"
              categoryKey={ASSET_CATEGORY.IMAGE}
              btnText="Image"
              dataSelector={({ id, url }) => ({
                id,
                url
              })}
            />
          </Form.Item>
        )}
      </Space>
      <Button
        className="text-btn mb-12"
        type="text"
        size="small"
        onClick={() => remove(name)}
      >
        Remove Item
      </Button>
    </>
  );
};

const CTAGridCarouselForm = ({
  form: { type: formType, moduleId, defaultValues, index: order },
  pageId,
  type,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const isEdit = formType === FORM_TYPES.EDIT;
  const [addEditModule, { loading }] = useMutation(
    isEdit ? UPDATE_PAGE_MODULE : CREATE_PAGE_MODULE
  );

  const hasButtonIcon = [MODULE_TYPES.CTA_BLOCK, MODULE_TYPES.HERO].includes(
    type
  );

  useEffect(() => {
    if (moduleId && defaultValues && formType === FORM_TYPES.EDIT && form) {
      form.setFieldsValue({
        title: defaultValues?.title ?? '',
        description: defaultValues?.description ?? '',
        status: defaultValues?.status ?? STATUS_TYPES.DRAFT,
        permissions:
          defaultValues?.permissions?.map((value) => ({
            label: value,
            value
          })) ?? [],
        settings: {
          ...initialValues.settings,
          ...defaultValues?.moduleData?.settings
        },
        config: {
          type: defaultValues?.moduleData?.config?.[MODULE_TYPE_KEYS[type]],
          items: defaultValues?.moduleData?.config?.items?.map((item) => ({
            asset: item?.asset,
            description: item?.description,
            internalPageType: item?.internalPageType,
            type: item?.type,
            title: item?.title,
            url: item?.url,
            ...(type === MODULE_TYPES.CTA_BUTTON_LIST && {
              buttonType: item?.buttonType
            }),
            ...(item?.type === ACTION_TYPES.INTERNAL && {
              ...(item?.internalPageType === PAGE_TYPES.STATIC
                ? { slug: item?.url }
                : {
                    slug: item?.url
                      ? {
                          label: item?.url,
                          value: item?.url
                        }
                      : null
                  })
            })
          }))
        }
      });
    }
  }, [
    form,
    moduleId,
    formType,
    type,
    defaultValues,
    form,
    initialValues,
    hasButtonIcon
  ]);

  const handleSubmit = (values) => {
    const key = MODULE_KEYS[type];
    if (!key) return;
    const { config, permissions, settings, ...rest } = values ?? {};

    const payload = {
      ...(!isEdit && {
        type,
        order: order + 1
      }),
      permissions: permissions?.map(({ value }) => value),
      ...rest,
      [key]: {
        settings,
        config: {
          type: config?.type,
          items: config?.items?.map((item) => ({
            assetId: item?.asset?.id,
            description: item?.description,
            title: item?.title,
            internalPageType: item?.internalPageType,
            type: item?.type,
            url: item?.url,
            ...(type === MODULE_TYPES.CTA_BUTTON_LIST && {
              buttonType: item?.buttonType
            })
          }))
        }
      }
    };

    addEditModule({
      variables: { data: payload, id: isEdit ? moduleId : pageId }
    }).then(
      ({
        data: {
          addUpdatedPageModule: { pageModule }
        }
      }) => {
        onSuccess(pageModule);
      }
    );
  };
  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <ModuleFields />
      <Space className="w-full" direction="vertical">
        <Space className="w-full" direction="vertical">
          <ShowFields settings={SETTINGS} type={type} />
          <Typography.Text>{CONFIG_TITLE[type]}</Typography.Text>
          <Form.List name={['config', 'items']}>
            {(fields, { add, remove }) => (
              <>
                {fields?.map((field) => (
                  <ItemInput
                    form={form}
                    remove={remove}
                    key={field?.key}
                    type={type}
                    {...field}
                  />
                ))}
                <Form.Item>
                  <Button
                    className="text-btn"
                    type="text"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    Add Items
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Space>
        <Permissions />
        <div className="d-flex button-section">
          <Space>
            <Form.Item>
              <Button
                type="text"
                htmlType="submit"
                className="text-btn mr-8"
                size="middle"
                disabled={loading}
                loading={loading}
              >
                Save
              </Button>
            </Form.Item>

            <Form.Item>
              <Button
                disabled={loading}
                onClick={onCancel}
                type="text"
                className="text-btn2"
              >
                Cancel
              </Button>
            </Form.Item>
          </Space>
        </div>
      </Space>
    </Form>
  );
};

export default CTAGridCarouselForm;
